import React, { useState } from "react";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomToggleSwitch from "../../../../components/common/CustomToggle";
import { useToggleState } from "../../../../Hooks/useToggleState";
import CustomModal from "../../../../components/common/Modal";
import { useDispatch } from "react-redux";
import {
   addSingleFleetChildAsync,
   fetchSingleFleetAsync,
   updateSingleFleetChildAsync,
} from "../../../../redux/features/fleetMaster";
import { useParams } from "react-router-dom";
import CustomTable from "../../../../components/common/CustomTable";
import { BsPencilSquare } from "react-icons/bs";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { Form, FormikProvider, useFormik } from "formik";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";

const validationSchema = Yup.object().shape({
   amount: Yup.number().required("Amount is required"),
   toDate: Yup.date()
      .required("To Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   fromDate: Yup.date()
      .required("From Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
});

const initialValues = {
   id: "",
   amount: "",
   toDate: "",
   fromDate: "",
};

function MonthlyMaintenanceBudget({ maintenanceBudget }) {
   const params = useParams();
   const dispatch = useDispatch();

   const [action, setAction] = useState("");
   const [isModalVisible, handleShowModal, handleCloseModal] = useToggleState();

   const columns = [
      {
         name: "Amount",
         selector: (row) => row.amount,
      },
      {
         name: "To Date",
         selector: (row) => row.toDate,
         sortable: true,
      },
      {
         name: "From Date",
         selector: (row) => {
            return row.fromDate;
         },
         sortable: true,
      },
      {
         name: "Action",
         width: "9rem",
         selector: (row) => row.action,
      },
   ];

   const data = maintenanceBudget?.map((item) => ({
      ...item,
      action: (
         <button
            className="btn"
            onClick={() => {
               setAction("edit");
               setValues({
                  id: item?.id,
                  amount: item?.amount,
                  toDate: item?.toDate,
                  fromDate: item?.fromDate,
               });
               handleShowModal();
            }}
            type="button"
         >
            <BsPencilSquare
               className="text-primary"
               style={{ height: "1.5rem", width: "1.5rem" }}
            />
         </button>
      ),
   }));

   const handleAddOrUpdateMonthlyBudget = (formData) => {
      const newData = new FormData();

      const data = {
         fleet_master_id: params?.id,
         monthly_maintenance_budget_id: formData.id,
         model_id: 3,
      };
      const dataArr = {
         monthly_maintenance_budget_amount: [formData.amount],
         to_date: [formData.toDate],
         from_date: [formData.fromDate],
      };

      Object.keys(data).forEach((item) => {
         newData.append(item, data[item]);
      });
      Object.keys(dataArr).forEach((item) => {
         newData.append(`${item}[]`, JSON.stringify(dataArr[item]));
      });

      if (action === "edit") {
         newData.append("_method", "PATCH");
         newData.append("action_id", 2);

         dispatch(backdropLoadingAction(true));
         dispatch(
            updateSingleFleetChildAsync({
               data: newData,
               err: () => {
                  dispatch(backdropLoadingAction(false));
                  toast.error("Something went wrong !");
               },
               done: () => {
                  dispatch(fetchSingleFleetAsync({ id: params?.id }));
                  dispatch(backdropLoadingAction(false));
                  toast.success("Record updated successfully !");
                  handleCloseModal();
               },
            })
         );
      }

      if (action === "add") {
         newData.append("action_id", 1);

         dispatch(backdropLoadingAction(true));
         dispatch(
            addSingleFleetChildAsync({
               data: newData,
               err: () => {
                  dispatch(backdropLoadingAction(false));
                  toast.error("Something went wrong !");
               },
               done: () => {
                  dispatch(fetchSingleFleetAsync({ id: params?.id }));
                  dispatch(backdropLoadingAction(false));
                  toast.success("Record added successfully !");
                  handleCloseModal();
               },
            })
         );
      }
   };

   const formik = useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: handleAddOrUpdateMonthlyBudget,
   });

   const { errors, touched, getFieldProps, setValues, resetForm, handleSubmit } = formik;

   return (
      <>
         <CustomModal
            showModal={isModalVisible}
            handleCloseModal={handleCloseModal}
            modalSize="modal-xl"
            title="Fleet"
            onSubmit={handleSubmit}
            child={
               <FormikProvider value={formik}>
                  <Form noValidate>
                     <div
                        className="tab-pane fade show active"
                        id="4"
                        role="tabpanel"
                        aria-labelledby="4"
                     >
                        <div className="col-lg-12">
                           <div className="card card-primary">
                              <div className="card-header">
                                 <h3 className="card-title">
                                    Monthly Maintenance Budget
                                 </h3>
                              </div>

                              <div className="card-body">
                                 <div className={"row border-bottom mb-4 pb-3"}>
                                    <div className="col-lg-4">
                                       <CustomInput
                                          require
                                          label="Amount"
                                          id="fitnessAmount"
                                          placeholder="Enter Amount"
                                          {...getFieldProps("amount")}
                                          errors={
                                             Boolean(touched.amount) &&
                                             Boolean(errors.amount)
                                          }
                                          message={errors.amount}
                                       />
                                    </div>
                                    <div className="col-lg-4">
                                       <CustomDatePicker
                                          require
                                          label="To Date"
                                          id="serviceDate"
                                          placeholder="Select To Date"
                                          {...getFieldProps("toDate")}
                                          errors={
                                             Boolean(touched.toDate) &&
                                             Boolean(errors.toDate)
                                          }
                                          message={errors.toDate}
                                       />
                                    </div>
                                    <div className="col-lg-4">
                                       <CustomDatePicker
                                          require
                                          label="From Date"
                                          id="serviceDate"
                                          placeholder="Select From Date"
                                          {...getFieldProps("fromDate")}
                                          errors={
                                             Boolean(touched.fromDate) &&
                                             Boolean(errors.fromDate)
                                          }
                                          message={errors.fromDate}
                                       />
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </Form>
               </FormikProvider>
            }
         />

         <CustomTable columns={columns} data={data} />

         <div className="row mt-3">
            <div className="col-lg-6">
               <button
                  type="button"
                  className="btn btn-primary float-left"
                  onClick={() => {
                     setAction("add");
                     resetForm();
                     handleShowModal();
                  }}
               >
                  <i className="fas fa-plus"></i> Add More
               </button>
            </div>
         </div>
      </>
   );
}

export default MonthlyMaintenanceBudget;
